import React, { useState, useRef } from "react";
import axios from "axios";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useTheme } from "styled-components";
import { Upload, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { cn } from "../../../lib/utils";

const apiBase = process.env.REACT_APP_API_URL || "http://localhost:3001";

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export default function UploadCsvTab() {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  // Step 1 = file upload, Step 2 = preview & confirm, Step 3 = processing / done
  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile?.name?.toLowerCase().endsWith(".csv")) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setMessage(null);
    } else if (selectedFile) {
      setFile(null);
      setFileName("");
      setMessage("Please select a .csv file");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile?.name?.toLowerCase().endsWith(".csv")) {
      setFile(droppedFile);
      setFileName(droppedFile.name);
      setMessage(null);
    } else if (droppedFile) {
      setMessage("Please drop a .csv file");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  // Step 1: Validate CSV and get preview data
  const handleValidate = async () => {
    if (!file) {
      setMessage("Please select a CSV file first");
      return;
    }
    setUploading(true);
    setMessage(null);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append("file", file);

      const resp = await axios.post(`${apiBase}/admin/ValidateContactList`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      setPreviewData(resp.data);
      setStep(2);
    } catch (err) {
      const data = err.response?.data;
      if (data?.reason === "missing_required_columns") {
        setMessage(`Missing required column(s): ${data.missing?.join(", ")}. Your CSV must include at least First Name and Last Name.`);
      } else if (data?.reason === "missing_required_values") {
        let msg = `${data.invalidCount} of ${data.totalCount} contact(s) are missing required values (First Name or Last Name).`;
        if (data.examples?.length) {
          const exList = data.examples.map(
            (ex) => `Row ${ex.row}: missing ${ex.missingFields.join(", ")}`
          );
          msg += ` Examples: ${exList.join("; ")}.`;
        }
        setMessage(msg);
      } else {
        setMessage(data?.error || data?.message || "Validation failed");
      }
    } finally {
      setUploading(false);
    }
  };

  // Step 2: Confirm and upload contacts → transitions to step 3 (processing)
  const handleConfirmUpload = async () => {
    if (!file) return;
    setSubmitting(true);
    setMessage(null);
    setUploadResult(null);
    setStep(3); // show processing screen immediately
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append("file", file);

      const resp = await axios.post(`${apiBase}/admin/UploadContactList`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });

      setUploadResult({
        success: true,
        totalUploaded: resp.data.totalUploaded,
      });
    } catch (err) {
      setUploadResult({
        success: false,
        error: err.response?.data?.error || err.response?.data?.message || "Upload failed",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileName("");
    setMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCancel = () => {
    setStep(1);
    setPreviewData(null);
    setUploadResult(null);
    clearFile();
  };

  const handleStartOver = () => {
    setStep(1);
    setPreviewData(null);
    setUploadResult(null);
    clearFile();
  };

  return (
    <div className="space-y-4 mt-4">
      {/* ── Step 1: File Upload ─────────────────────────────────────────── */}
      {step === 1 && (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
            !isDragging && !file && "border-border"
          )}
          style={
            file
              ? { borderColor: theme.colors.success, backgroundColor: theme.colors.successBg }
              : isDragging
              ? { borderColor: theme.colors.primary, backgroundColor: `${theme.colors.primary}0D` }
              : undefined
          }
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="admin-csv-input"
          />
          <label htmlFor="admin-csv-input" className="cursor-pointer block">
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {fileName || "Drag and drop a CSV file here, or click to browse"}
            </p>
          </label>
          {fileName && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button
                size="sm"
                onClick={handleValidate}
                disabled={uploading}
                className="shadow-sm hover:opacity-90"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }}
              >
                {uploading ? <LoadingSpinner /> : "Upload & Validate"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={clearFile}
                style={{ backgroundColor: theme.colors.error, color: theme.colors.buttonTextOnPrimary }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: Preview & Confirm ──────────────────────────────────── */}
      {step === 2 && previewData && (
        <div className="space-y-4">
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
            onClick={handleCancel}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              <strong>{previewData.totalContacts}</strong> contact{previewData.totalContacts !== 1 ? "s" : ""} will be uploaded
            </p>
            <p className="text-xs text-muted-foreground">
              File: {fileName}
            </p>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-muted/80 backdrop-blur-sm">
                    <th className="text-left px-4 py-2.5 font-medium">Property Key</th>
                    <th className="text-left px-4 py-2.5 font-medium">CSV Column</th>
                    <th className="text-left px-4 py-2.5 font-medium">Sample Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {previewData.propertyKeys.map((key, i) => (
                    <tr key={key} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2 font-mono text-xs">{key}</td>
                      <td className="px-4 py-2">{previewData.columns[i]}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {previewData.firstContact?.[key] ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleConfirmUpload}
              disabled={submitting}
              className="shadow-sm hover:opacity-90"
              style={{ backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }}
            >
              {submitting ? <LoadingSpinner /> : "Submit"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3: Processing / Confirmation ────────────────────────── */}
      {step === 3 && (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          {submitting ? (
            <>
              <LoadingSpinner />
              <p className="text-sm text-muted-foreground">
                Processing {previewData?.totalContacts ?? ""} contact{previewData?.totalContacts !== 1 ? "s" : ""}...
              </p>
              <p className="text-xs text-muted-foreground">
                This may take a moment. Please don't close this page.
              </p>
            </>
          ) : uploadResult?.success ? (
            <>
              <div
                className="rounded-full p-3"
                style={{ backgroundColor: theme.colors.successBg }}
              >
                <CheckCircle className="h-8 w-8" style={{ color: theme.colors.success }} />
              </div>
              <p className="text-sm font-medium">
                Successfully uploaded {uploadResult.totalUploaded} contact{uploadResult.totalUploaded !== 1 ? "s" : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                All contacts have been added to the database.
              </p>
              <Button
                size="sm"
                onClick={handleStartOver}
                className="mt-2 shadow-sm hover:opacity-90"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }}
              >
                Upload Another File
              </Button>
            </>
          ) : uploadResult ? (
            <>
              <div
                className="rounded-full p-3"
                style={{ backgroundColor: theme.colors.errorBg }}
              >
                <Upload className="h-8 w-8" style={{ color: theme.colors.error }} />
              </div>
              <p className="text-sm font-medium" style={{ color: theme.colors.error }}>
                Upload failed
              </p>
              <p className="text-xs text-muted-foreground">
                {uploadResult.error}
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={handleConfirmUpload}
                  className="shadow-sm hover:opacity-90"
                  style={{ backgroundColor: theme.colors.primary, color: theme.colors.buttonTextOnPrimary }}
                >
                  Retry
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleStartOver}
                >
                  Start Over
                </Button>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ── Shared message display (steps 1 & 2 only) ────────────────── */}
      {step !== 3 && message && (
        <p
          className="text-sm p-3 rounded-md"
          style={
            message.includes("Successfully")
              ? { backgroundColor: theme.colors.successBg, color: theme.colors.success }
              : { backgroundColor: theme.colors.errorBg, color: theme.colors.error }
          }
        >
          {message}
        </p>
      )}
    </div>
  );
}
